import { Slot } from '@radix-ui/react-slot'
import type { ButtonHTMLAttributes } from 'react'
import React from 'react'
import { tv, type VariantProps } from 'tailwind-variants'

import { Load } from './load'

const buttonVariants = tv({
  base: [
    'inline-flex items-center justify-center rounded-lg px-6 text-sm 2xl:text-base font-semibold outline-hidden cursor-pointer',
    'focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-bios-primary',
    'disabled:cursor-not-allowed disabled:border-gray-500 disabled:bg-gray-500 disabled:text-white',

    'active:opacity-80 transition-all',
  ],
  variants: {
    variant: {
      primary: [
        'bg-bios-primary border-bios-primary border text-white',
        'hover:border-bios-secondary hover:bg-bios-secondary',
        'aria-selected:bg-bios-primary aria-selected:border-bios-primary',
      ],
      secondary: [
        'bg-white text-bios-secondary border-transparent border',
        'hover:border-bios-primary hover:bg-bios-primary/30',
        'aria-selected:border-bios-primary aria-selected:bg-bios-primary/30',
      ],
      tertiary: [
        'bg-white text-bios-secondary border-transparent border',
        'border-bios-secondary bg-bios-green-200',
        'aria-selected:border-bios-secondary aria-selected:bg-bios-green-200',
        'hover:bg-bios-primary hover:border-bios-primary hover:text-white',
      ],
      quaternary: [
        'bg-white text-bios-secondary border-transparent border',
        'hover:bg-bios-monochromatic-200 hover:text-bios-secondary',
        'aria-selected:border-bios-primary aria-[selected=true]:bg-bios-monochromatic-200 hover:text-bios-secondary',
      ],
      outline: [
        'bg-transparent text-bios-600 border-bios-primary border',
        'hover:border-bios-primary hover:bg-bios-300/20',
        'aria-selected:border-bios-primary aria-selected:bg-bios-300/20',
      ],
      ghost: [
        'bg-transparent border border-transparent font-medium text-monochromatic-400',
        'hover:border-zinc-200 hover:bg-zinc-200',
        'disabled:bg-monochromatic-200 disabled:border-none disabled:text-monochromatic-300',
        'aria-selected:border-zinc-200 aria-selected:bg-zinc-200',
      ],
    },
    size: {
      default: 'h-10 2xl:h-12',
      big: 'h-auto',
      icon: 'size-10 p-0',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'default',
  },
})

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  isSubmitting?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isSubmitting = false,
      disabled = false,
      ...props
    },
    ref,
  ) => {
    const Component = asChild ? Slot : 'button'

    return (
      <Component
        className={buttonVariants({ variant, size, className })}
        {...props}
        ref={ref}
        disabled={disabled || isSubmitting}
      >
        {isSubmitting ? <Load /> : props.children}
      </Component>
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
